import { useEffect, useState, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { IconButton } from '@/shared/ui/core/IconButton';
import { LoadingOverlay } from '@/shared/ui/core/Loading';
import { ModalContext, useModalContext } from './modalContext';

export type ModalSize =
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | 'small'
  | 'medium'
  | 'large'
  | 'custom';

export type ModalHeight = 'default' | 'tall';

export type ModalIconTone = 'primary' | 'neutral' | 'info' | 'success' | 'warning' | 'danger';
export type ModalIconVariant = 'badge' | 'inline';

export type ModalHeaderTone =
  | 'default'
  | 'neutral'
  | 'create'
  | 'edit'
  | 'status'
  | 'primary'
  | 'warning'
  | 'info'
  | 'success'
  | 'danger';

export const modalIconBadgeToneClass: Record<ModalIconTone, string> = {
  primary: 'bg-primary-soft text-primary border-primary/20',
  neutral: 'bg-bg text-text-secondary border-border',
  info: 'bg-info-soft text-info-fg border-info/20',
  success: 'bg-success-soft text-success-fg border-success/20',
  warning: 'bg-warning-soft text-warning-fg border-warning/20',
  danger: 'bg-danger-soft text-danger-fg border-danger/20',
};

export const modalHeaderToneClass: Record<ModalHeaderTone, string> = {
  default: 'bg-surface border-b border-border',
  neutral: 'bg-surface border-b border-border',
  create:
    'bg-primary text-white border-b border-primary-hover shadow-xs [&_.text-text]:text-white [&_.text-text-secondary]:text-white/80 [&_.text-text-muted]:text-white/70 [&_.bg-primary-soft]:bg-white/20 [&_.text-primary]:text-white [&_.bg-warning-soft]:bg-white/20 [&_.text-warning-fg]:text-white [&_.text-warning]:text-white',
  primary:
    'bg-primary text-white border-b border-primary-hover shadow-xs [&_.text-text]:text-white [&_.text-text-secondary]:text-white/80 [&_.text-text-muted]:text-white/70 [&_.bg-primary-soft]:bg-white/20 [&_.text-primary]:text-white [&_.bg-warning-soft]:bg-white/20 [&_.text-warning-fg]:text-white [&_.text-warning]:text-white',
  edit:
    'bg-warning text-white border-b border-warning-fg/30 shadow-xs [&_.text-text]:text-white [&_.text-text-secondary]:text-white/85 [&_.text-text-muted]:text-white/75 [&_.bg-primary-soft]:bg-white/20 [&_.text-primary]:text-white [&_.bg-warning-soft]:bg-white/20 [&_.text-warning-fg]:text-white [&_.text-warning]:text-white',
  status:
    'bg-warning text-white border-b border-warning-fg/30 shadow-xs [&_.text-text]:text-white [&_.text-text-secondary]:text-white/85 [&_.text-text-muted]:text-white/75 [&_.bg-primary-soft]:bg-white/20 [&_.text-primary]:text-white [&_.bg-warning-soft]:bg-white/20 [&_.text-warning-fg]:text-white [&_.text-warning]:text-white',
  warning:
    'bg-warning text-white border-b border-warning-fg/30 shadow-xs [&_.text-text]:text-white [&_.text-text-secondary]:text-white/80 [&_.text-text-muted]:text-white/70 [&_.bg-primary-soft]:bg-white/20 [&_.text-primary]:text-white [&_.bg-warning-soft]:bg-white/20 [&_.text-warning-fg]:text-white',
  info:
    'bg-info text-white border-b border-info-fg/30 shadow-xs [&_.text-text]:text-white [&_.text-text-secondary]:text-white/80 [&_.text-text-muted]:text-white/70 [&_.bg-primary-soft]:bg-white/20 [&_.text-primary]:text-white',
  success:
    'bg-primary text-white border-b border-primary-hover shadow-xs [&_.text-text]:text-white [&_.text-text-secondary]:text-white/80 [&_.text-text-muted]:text-white/70 [&_.bg-primary-soft]:bg-white/20 [&_.text-primary]:text-white',
  danger:
    'bg-danger text-white border-b border-danger-fg/30 shadow-xs [&_.text-text]:text-white [&_.text-text-secondary]:text-white/80 [&_.text-text-muted]:text-white/70 [&_.bg-primary-soft]:bg-white/20 [&_.text-primary]:text-white',
};

export type ModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** Optional icon in front of title (kích thước = icon X: size-4). */
  icon?: ReactNode;
  /** Presentation style: 'badge' (soft-tinted rounded box matching close X button) or 'inline' (naked glyph). Default 'badge'. */
  iconVariant?: ModalIconVariant;
  /** Color theme for icon badge/glyph. Default 'primary'. */
  iconTone?: ModalIconTone;
  /** Optional custom class for title icon. */
  iconClassName?: string;
  /** Header tone theme: 'create' for soft green, 'edit' for soft orange/amber, or 'neutral'/'default'. */
  headerTone?: ModalHeaderTone;
  /** Optional custom classes for modal header bar. */
  headerClassName?: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Standard modal sizes:
   * - 'small' | 'sm': max-w-lg (~512px) for compact confirmations, single input prompts.
   * - 'medium' | 'md': max-w-2xl (~672px) for standard forms, alerts, single/two-column dialogs.
   * - 'large' | 'lg': max-w-4xl (~896px) for 2-column forms, standard entity create/edit.
   * - 'xl': max-w-5xl (~1024px) for master-detail, line item repeaters.
   * - '2xl': max-w-[min(80rem,calc(100%-2rem))] (~1280px) for full preview/wizards.
   * - 'custom': relies on `customWidth` (e.g. '750px', '85vw') or `className`.
   */
  size?: ModalSize;
  /** Custom width style (e.g. '720px', '80vw') when using size='custom' or overriding defaults */
  customWidth?: string;
  /** Inline style overrides for the dialog panel */
  style?: React.CSSProperties;
  /** Viewport height budget. `tall` for document / print preview. */
  height?: ModalHeight;
  /** When false, Escape / X do not close. Default true. */
  dismissible?: boolean;
  /** When true (default for ERP), overlay click does not close. Close via X, buttons or Esc. */
  preventOutsideClose?: boolean;
  /** Use `alertdialog` for destructive / conflict confirms (P8.3). */
  role?: 'dialog' | 'alertdialog';
  /** Extra classes on the dialog panel. */
  className?: string;
  /** Extra classes on the scrollable body (default padded). */
  bodyClassName?: string;
  /** Covers the dialog with a labeled spinner while a save is in flight. */
  loading?: boolean;
  loadingLabel?: ReactNode;
  /** When true, modal fills entire screen (fixed inset-0), suitable for mobile scanner or full viewers. */
  fullScreen?: boolean;
};

const sizeClass: Record<ModalSize, string> = {
  sm: 'max-w-lg',
  small: 'max-w-lg',
  md: 'max-w-2xl',
  medium: 'max-w-2xl',
  lg: 'max-w-4xl',
  large: 'max-w-4xl',
  xl: 'max-w-5xl',
  '2xl': 'max-w-[min(80rem,calc(100%-2rem))]',
  custom: '',
};

const heightClass: Record<ModalHeight, string> = {
  default: 'max-h-[min(90dvh,54rem)]',
  tall: 'max-h-[min(94dvh,64rem)]',
};

/**
 * Shared centered dialog shell (Radix Dialog).
 * Prefer domain wrappers: `EntityFormModal` (CRUD), `ContentModal` (preview/viewer),
 * `ConfirmationDialog` (destructive confirm).
 */
export function Modal({
  open,
  onOpenChange,
  title,
  icon,
  iconVariant = 'badge',
  iconTone = 'primary',
  iconClassName,
  headerTone = 'default',
  headerClassName,
  description,
  children,
  footer,
  size = 'md',
  customWidth,
  style,
  height = 'default',
  dismissible = true,
  preventOutsideClose = true,
  role = 'dialog',
  className,
  bodyClassName,
  loading = false,
  loadingLabel = 'Đang lưu...',
  fullScreen = false,
}: ModalProps) {
  const blockOutside = !dismissible || preventOutsideClose;
  const resolvedMaxWidth = customWidth ? customWidth : undefined;

  // Lock background scroll on both html and body when modal is open
  useEffect(() => {
    if (!open) return;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, [open]);

  const parentModal = useModalContext();
  const depth = (parentModal?.depth ?? -1) + 1;
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);
  const zModalVal = depth > 0 ? `calc(var(--z-modal) + ${depth * 10})` : 'var(--z-modal)';
  const isDarkHeader = headerTone !== 'default' && headerTone !== 'neutral';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          style={{ zIndex: zModalVal }}
          className="fixed inset-0 bg-[var(--color-overlay)] overscroll-none"
          onWheel={(e) => e.preventDefault()}
          onTouchMove={(e) => e.preventDefault()}
          onClick={blockOutside ? (e) => e.preventDefault() : undefined}
        />
        <Dialog.Content
          ref={setContentElement}
          role={role}
          style={{
            zIndex: zModalVal,
            ...(fullScreen ? {} : resolvedMaxWidth ? { maxWidth: resolvedMaxWidth } : {}),
            ...style,
          }}
          className={cn(
            fullScreen
              ? 'fixed inset-0 z-[var(--z-modal)] flex flex-col w-full h-full max-w-none translate-x-0 translate-y-0 top-0 left-0 rounded-none'
              : 'fixed top-1/2 left-1/2 flex w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col',
            'overflow-visible bg-transparent focus:outline-none transition-[max-width] duration-200',
            !fullScreen && sizeClass[size],
            className,
          )}
          onEscapeKeyDown={dismissible ? undefined : (e) => e.preventDefault()}
          onInteractOutside={blockOutside ? (e) => e.preventDefault() : undefined}
        >
          <ModalContext.Provider value={{ depth, container: contentElement }}>
            <div
              className={cn(
                'flex min-h-0 w-full flex-col overflow-hidden bg-surface',
                fullScreen
                  ? 'h-full flex-1 max-h-none rounded-none shadow-none'
                  : cn('rounded-modal shadow-3', heightClass[height]),
              )}
            >
              <div
                className={cn(
                  'flex shrink-0 justify-between gap-3 px-4 py-3 transition-colors',
                  modalHeaderToneClass[headerTone],
                  description ? 'items-start' : 'items-center',
                  headerClassName,
                )}
              >
                  <div className={cn('flex min-w-0 flex-1 gap-3', description ? 'items-start' : 'items-center')}>
                    {icon ? (
                      iconVariant === 'badge' ? (
                        <div
                          className={cn(
                            'flex size-9 shrink-0 items-center justify-center rounded-control border transition-colors',
                            isDarkHeader
                              ? 'bg-white/20 text-white border-white/25 shadow-xs [&>svg]:text-white'
                              : modalIconBadgeToneClass[iconTone],
                            description && 'mt-0.5',
                            iconClassName,
                          )}
                          aria-hidden
                        >
                          <span className="inline-flex size-4 items-center justify-center [&>svg]:size-4">
                            {icon}
                          </span>
                        </div>
                      ) : (
                        <span
                          className={cn(
                            'inline-flex size-4 shrink-0 items-center justify-center [&>svg]:size-4',
                            isDarkHeader
                              ? 'text-white [&>svg]:text-white'
                              : [
                                  'text-primary',
                                  iconTone === 'danger' && 'text-danger-fg',
                                  iconTone === 'warning' && 'text-warning-fg',
                                  iconTone === 'info' && 'text-info-fg',
                                  iconTone === 'success' && 'text-success-fg',
                                  iconTone === 'neutral' && 'text-text-secondary',
                                ],
                            description && 'mt-1',
                            iconClassName,
                          )}
                          aria-hidden
                        >
                          {icon}
                        </span>
                      )
                    ) : null}

                    <div className="min-w-0 flex-1">
                      {typeof title === 'string' ? (
                        <Dialog.Title
                          className={cn(
                            'm-0 text-base font-bold leading-snug sm:text-lg',
                            isDarkHeader ? 'text-white' : 'text-text',
                          )}
                        >
                          {title}
                        </Dialog.Title>
                      ) : (
                        <Dialog.Title asChild>
                          <div
                            className={cn(
                              'm-0 text-base font-bold leading-snug sm:text-lg',
                              isDarkHeader ? 'text-white' : 'text-text',
                            )}
                          >
                            {title}
                          </div>
                        </Dialog.Title>
                      )}
                      {description ? (
                        typeof description === 'string' ? (
                          <Dialog.Description
                            className={cn(
                              'mt-0.5 mb-0 text-caption',
                              isDarkHeader ? 'text-white/80' : 'text-text-secondary',
                            )}
                          >
                            {description}
                          </Dialog.Description>
                        ) : (
                          <Dialog.Description asChild>
                            <div
                              className={cn(
                                'mt-0.5 text-caption',
                                isDarkHeader ? 'text-white/80' : 'text-text-secondary',
                              )}
                            >
                              {description}
                            </div>
                          </Dialog.Description>
                        )
                      ) : (
                        <Dialog.Description className="sr-only">
                          {typeof title === 'string' ? title : 'Modal Dialog'}
                        </Dialog.Description>
                      )}
                    </div>
                  </div>
                  {dismissible ? (
                    <Dialog.Close asChild>
                      <IconButton
                        label="Đóng"
                        icon={<X className="size-5" />}
                        size="md"
                        tooltip={false}
                        className={cn(
                          'size-9 shrink-0',
                          isDarkHeader
                            ? 'text-white/80 hover:text-white hover:bg-white/15 active:bg-white/20'
                            : undefined,
                        )}
                        iconClassName="size-5 [&>svg]:size-5"
                      />
                    </Dialog.Close>
                  ) : null}
                </div>
            <div
              className={cn(
                'min-h-0 flex-1 overflow-y-auto px-4 py-4 scrollbar-thin overscroll-contain',
                fullScreen && 'flex flex-col',
                bodyClassName,
              )}
            >
              {children}
            </div>
            {footer ? (
              <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-border px-3 py-3">
                {footer}
              </div>
            ) : null}
            <LoadingOverlay visible={loading} label={loadingLabel} />
          </div>
        </ModalContext.Provider>
      </Dialog.Content>
    </Dialog.Portal>
    </Dialog.Root>
  );
}
